import React, { useState, useEffect, useRef } from 'react';
import { proposalApi, CreateProposalRequest, Proposal } from '../api/proposalApi';
import { toast } from 'react-toastify';
import Spinner from './Spinner';
import { Save, Send, Bold, Italic, List, ListOrdered, Heading1, Heading2, Table as TableIcon, Eye, Code, HelpCircle, Minus, Quote } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { BrandingData } from '../api/brandingApi';

interface ProposalWithDate extends Proposal {
  createdDate?: string;
}

interface Props {
  onProposalCreated: () => void;
  branding?: BrandingData | null;
  initialData?: ProposalWithDate | null;
}

const cleanMarkdown = (text: string) => {
  if (!text) return '';
  return text
    .replace(/__##/g, '##')      // Fix broken headings
    .replace(/##(\d)/g, '## $1') // Fix missing space in headings
    // Ensure headers have a blank line before them for proper block parsing
    .replace(/([^\n])\n(#{1,2}\s+)/g, '$1\n\n$2')
    // Tighten section breaks
    .replace(/\n---/g, '\n\n---\n\n');
};

const STANDARD_DOCUMENT_TYPES = [
  "Business Proposal",
  "Quotation",
  "Order Form",
  "Sales Order",
  "Estimate",
  "Service Order",
  "Renewal Proposal",
  "Service Agreement",
  "Statement of Work",
  "Project Proposal",
  "Contract",
  "Engagement Letter",
  "Change Order",
  "Invoice",
  "Pro Forma Invoice",
  "Credit Note",
  "Recruitment Proposal",
  "Recruitment Agreement",
  "Candidate Submission",
  "Job Offer Letter",
  "Confirmation Letter",
  "Welcome / Onboarding",
];

export default function ProposalForm({ onProposalCreated, branding, initialData }: Props) {
  const [loading, setLoading] = useState(false);
  const [expiryDisplay, setExpiryDisplay] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [declining, setDeclining] = useState(false);

  const publicLink = initialData?.publicToken
    ? `${window.location.origin}/proposal/${initialData.publicToken}`
    : null;

  const handleDecline = async () => {
    if (!initialData?.publicToken) return;
    setDeclining(true);
    try {
      await proposalApi.declineProposal(initialData.publicToken);
      toast.info("Proposal declined");
    } catch {
      // Handled by API wrapper
    } finally {
      setDeclining(false);
    }
  };

  const [customDocumentType, setCustomDocumentType] = useState('');

  const [formData, setFormData] = useState<CreateProposalRequest>({
    title: '',
    documentType: 'Business Proposal',
    customerName: '',
    customerEmail: '',
    validDays: 14,
    content: ''
  });

  useEffect(() => {
    if (!initialData) {
      return;
    }

    const savedDocumentType =
      initialData.documentType || "Business Proposal";

    const isCustomDocumentType =
      !STANDARD_DOCUMENT_TYPES.includes(savedDocumentType);

    setFormData({
      title: initialData.title || "",
      documentType: isCustomDocumentType
        ? "Other"
        : savedDocumentType,
      customerName: initialData.customerName || "",
      customerEmail: initialData.customerEmail || "",
      validDays: initialData.validDays || 14,
      content: initialData.content || "",
    });

    setCustomDocumentType(
      isCustomDocumentType
        ? savedDocumentType
        : ""
    );
  }, [initialData]);

  const getHeaderMarkdown = () => {
    let dateStr: string;
    
    if (initialData?.createdDate) {
      // Use createdDate from initialData if it exists (for edits)
      dateStr = new Date(initialData.createdDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } else {
      // Use current date for new proposals
      dateStr = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    }
    
    const titlePart = formData.title || '[Proposal Title]';
    const namePart = formData.customerName ? ` for ${formData.customerName}` : '';
    const fromName = branding?.companyName || 'Strivre Services';
    const documentType =
      formData.documentType === "Other"
        ? customDocumentType || "Custom Document"
        : formData.documentType;

    return `# ${titlePart}${namePart}

    **Type:** ${documentType || "Business Proposal"}  
    **Date:** ${dateStr}  
    **Prepared For:** ${formData.customerName || '[Client Name]'}  
    **From:** ${fromName}

    ---`;
  };

  const getFullMarkdown = () => {
    return `${getHeaderMarkdown()}\n\n${formData.content}`;
  };

  useEffect(() => {
    const date = new Date();
    date.setDate(date.getDate() + Number(formData.validDays));
    setExpiryDisplay(date.toLocaleDateString());
  }, [formData.validDays]);

  const insertMarkdown = (prefix: string, suffix: string = '') => {
    const textarea = textAreaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = formData.content;
    const before = text.substring(0, start);
    const selection = text.substring(start, end);
    const after = text.substring(end);

    const newContent = `${before}${prefix}${selection}${suffix}${after}`;
    setFormData({ ...formData, content: newContent });
    
    // Refocus and set cursor (minimal delay to allow state update)
    setTimeout(() => { textarea.focus(); textarea.setSelectionRange(start + prefix.length, end + prefix.length); }, 10);
  };

  const handleSubmit = async (
    sendImmediately: boolean
  ) => {
    if (
      !formData.title ||
      !formData.customerEmail ||
      !formData.content
    ) {
      return toast.warning(
        "Please fill in all required fields"
      );
    }

    const documentType =
      formData.documentType === "Other"
        ? customDocumentType.trim()
        : formData.documentType;

    if (!documentType) {
      return toast.warning(
        "Please select or enter a document type"
      );
    }

    setLoading(true);

    try {
      const payload = {
        ...formData,
        documentType,
        sendEmail: sendImmediately,
      };

      if (initialData?.id) {
        await proposalApi.updateProposal(
          initialData.id,
          payload
        );

        if (sendImmediately) {
          toast.success("Proposal updated and sent!");
        } else {
          toast.success("Proposal updated successfully");
        }
      } else {
        await proposalApi.createProposal(payload);

        if (sendImmediately) {
          toast.success("Proposal created and sent!");
        } else {
          toast.success("Proposal saved as draft");
        }
      }

      onProposalCreated();
    } catch {
      toast.error("Failed to save proposal");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-8 animate-fade-in">
      <div className="bg-white rounded-[2.5rem] border border-strivre-lightnavy shadow-sm overflow-hidden">
        <div className="p-6 sm:p-10 space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-2 text-left">
              <label className="text-sm font-semibold text-slate-700">Proposal Title</label>
              <input 
                className="input-base" 
                placeholder="e.g. Website Redesign Project"
                value={formData.title}
                onChange={e => setFormData({...formData, title: e.target.value})}
              />
            </div>
            <div className="space-y-2 text-left">
              <label className="text-sm font-semibold text-slate-700">
                Document Type
              </label>

              <select
                className="input-base"
                value={formData.documentType}
                onChange={(e) => {
                  const documentType = e.target.value;

                  setFormData({
                    ...formData,
                    documentType,
                  });

                  if (documentType !== "Other") {
                    setCustomDocumentType("");
                  }
                }}
              >
                <optgroup label="Sales & Commercial">
                  <option value="Business Proposal">
                    Business Proposal
                  </option>

                  <option value="Quotation">
                    Quotation
                  </option>

                  <option value="Order Form">
                    Order Form
                  </option>

                  <option value="Sales Order">
                    Sales Order
                  </option>

                  <option value="Estimate">
                    Estimate
                  </option>

                  <option value="Service Order">
                    Service Order
                  </option>

                  <option value="Renewal Proposal">
                    Renewal Proposal
                  </option>
                </optgroup>

                <optgroup label="Agreements & Projects">
                  <option value="Service Agreement">
                    Service Agreement
                  </option>

                  <option value="Statement of Work">
                    Statement of Work
                  </option>

                  <option value="Project Proposal">
                    Project Proposal
                  </option>

                  <option value="Contract">
                    Contract
                  </option>

                  <option value="Engagement Letter">
                    Engagement Letter
                  </option>

                  <option value="Change Order">
                    Change Order
                  </option>
                </optgroup>

                <optgroup label="Billing">
                  <option value="Invoice">
                    Invoice
                  </option>

                  <option value="Pro Forma Invoice">
                    Pro Forma Invoice
                  </option>

                  <option value="Credit Note">
                    Credit Note
                  </option>
                </optgroup>

                <optgroup label="Recruitment">
                  <option value="Recruitment Proposal">
                    Recruitment Proposal
                  </option>

                  <option value="Recruitment Agreement">
                    Recruitment Agreement
                  </option>

                  <option value="Candidate Submission">
                    Candidate Submission
                  </option>

                  <option value="Job Offer Letter">
                    Job Offer Letter
                  </option>
                </optgroup>

                <optgroup label="General">
                  <option value="Confirmation Letter">
                    Confirmation Letter
                  </option>

                  <option value="Welcome / Onboarding">
                    Welcome / Onboarding
                  </option>

                  <option value="Other">
                    Other / Custom Document
                  </option>
                </optgroup>
              </select>
            </div>
            {formData.documentType === "Other" && (
              <div className="space-y-2 text-left md:col-span-2">
                <label className="text-sm font-semibold text-slate-700">
                  Custom Document Type
                </label>

                <input
                  className="input-base"
                  placeholder="e.g. Master Services Agreement"
                  value={customDocumentType}
                  onChange={(e) =>
                    setCustomDocumentType(e.target.value)
                  }
                />
              </div>
            )}
            <div className="space-y-2 text-left">
              <label className="text-sm font-semibold text-slate-700">Client Name</label>
              <input 
                className="input-base" 
                placeholder="e.g. John Doe"
                value={formData.customerName}
                onChange={e => setFormData({...formData, customerName: e.target.value})}
              />
            </div>
            <div className="space-y-2 text-left">
              <label className="text-sm font-semibold text-slate-700">Client Email</label>
              <input 
                className="input-base" 
                type="email"
                placeholder="client@company.com"
                value={formData.customerEmail}
                onChange={e => setFormData({...formData, customerEmail: e.target.value})}
              />
            </div>
            <div className="space-y-2 text-left">
              <label className="text-sm font-semibold text-slate-700">Validity Period</label>
              <div className="flex items-center gap-4">
                <select 
                  className="input-base"
                  value={formData.validDays}
                  onChange={e => setFormData({...formData, validDays: Number(e.target.value)})}
                >
                  <option value={7}>7 Days</option>
                  <option value={14}>14 Days</option>
                  <option value={30}>30 Days</option>
                </select>
                <div className="text-[10px] font-bold text-slate-400 whitespace-nowrap bg-slate-50 px-4 py-3.5 rounded-xl border border-slate-100 uppercase tracking-tight">
                  Expires: {expiryDisplay}
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4 text-left border-t border-slate-50 pt-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <label className="text-sm font-semibold text-slate-700">Proposal Content</label>
              
              <div className="flex items-center gap-1 bg-strivre-softgrey p-1 rounded-xl border border-strivre-lightnavy">
                <button type="button" onClick={() => setShowPreview(false)} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${!showPreview ? 'bg-white text-strivre-burgundy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                  <Code size={14} /> Editor
                </button>
                <button type="button" onClick={() => setShowPreview(true)} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${showPreview ? 'bg-white text-strivre-burgundy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                  <Eye size={14} /> Preview
                </button>
              </div>
            </div>

            {!showPreview && (
              <div className="flex flex-wrap gap-2 mb-2 p-2 bg-slate-50 rounded-2xl border border-strivre-lightnavy">
                <button type="button" onClick={() => insertMarkdown('# ', '')} className="p-2 text-slate-500 hover:bg-white hover:text-strivre-burgundy rounded-lg transition-all" title="Heading 1"><Heading1 size={18} /></button>
                <button type="button" onClick={() => insertMarkdown('## ', '')} className="p-2 text-slate-500 hover:bg-white hover:text-strivre-burgundy rounded-lg transition-all" title="Heading 2"><Heading2 size={18} /></button>
                <div className="w-px h-6 bg-slate-200 mx-1 self-center" />
                <button type="button" onClick={() => insertMarkdown('**', '**')} className="p-2 text-slate-500 hover:bg-white hover:text-strivre-burgundy rounded-lg transition-all" title="Bold"><Bold size={18} /></button>
                <button type="button" onClick={() => insertMarkdown('_', '_')} className="p-2 text-slate-500 hover:bg-white hover:text-strivre-burgundy rounded-lg transition-all" title="Italic"><Italic size={18} /></button>
                <div className="w-px h-6 bg-slate-200 mx-1 self-center" />
                <button type="button" onClick={() => insertMarkdown('- ', '')} className="p-2 text-slate-500 hover:bg-white hover:text-strivre-burgundy rounded-lg transition-all" title="Bullet List"><List size={18} /></button>
                <button type="button" onClick={() => insertMarkdown('1. ', '')} className="p-2 text-slate-500 hover:bg-white hover:text-strivre-burgundy rounded-lg transition-all" title="Numbered List"><ListOrdered size={18} /></button>
                <button type="button" onClick={() => insertMarkdown('> ', '')} className="p-2 text-slate-500 hover:bg-white hover:text-strivre-burgundy rounded-lg transition-all" title="Blockquote"><Quote size={18} /></button>
                <button type="button" onClick={() => insertMarkdown('| Header | Header |\n| --- | --- |\n| Cell | Cell |', '')} className="p-2 text-slate-500 hover:bg-white hover:text-strivre-burgundy rounded-lg transition-all" title="Insert Table"><TableIcon size={18} /></button>
                <button type="button" onClick={() => insertMarkdown('\n---\n', '')} className="p-2 text-slate-500 hover:bg-white hover:text-strivre-burgundy rounded-lg transition-all" title="Section Break"><Minus size={18} /></button>
                <button type="button" onClick={() => setShowHelp(!showHelp)} className="ml-auto p-2 text-slate-500 hover:text-strivre-burgundy rounded-lg transition-all" title="Markdown Help"><HelpCircle size={18} /></button>
              </div>
            )}

            {showHelp && !showPreview && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-strivre-lightburgundy rounded-2xl border border-strivre-lightburgundy text-[10px] font-bold uppercase tracking-tight text-strivre-burgundy animate-fade-in">
                <div><span className="text-slate-400 mr-2"># Text</span> Heading 1</div>
                <div><span className="text-slate-400 mr-2">## Text</span> Heading 2</div>
                <div><span className="text-slate-400 mr-2">**Text**</span> Bold</div>
                <div><span className="text-slate-400 mr-2">---</span> Section Break</div>
                <div><span className="text-slate-400 mr-2">&gt; Text</span> Quote</div>
              </div>
            )}

           <div className="relative min-h-[400px]">
            {showPreview ? (
                <div
                className="
                    input-base min-h-[400px] p-8 
                    prose max-w-none bg-white overflow-y-auto text-left

                    prose-headings:!text-slate-900
                    prose-h1:!text-slate-900
                    prose-h2:!text-slate-900

                    prose-p:text-slate-600
                    prose-li:text-slate-600
                    prose-strong:text-slate-900

                    prose-headings:font-black
                    prose-headings:tracking-tight
                    prose-ul:list-disc prose-ul:pl-5
                    prose-ol:list-decimal prose-ol:pl-5
                "
                >
                <ReactMarkdown
                    remarkPlugins={[remarkGfm, remarkBreaks]}
                    components={{
                      h1: ({ children, ...props }) => <h1 style={{ color: "#0f172a" }} {...props}>{children}</h1>,
                      h2: ({ children, ...props }) => <h2 style={{ color: "#0f172a" }} {...props}>{children}</h2>,
                      h3: ({ children, ...props }) => <h3 style={{ color: "#0f172a" }} {...props}>{children}</h3>,
                      h4: ({ children, ...props }) => <h4 style={{ color: "#0f172a" }} {...props}>{children}</h4>,
                      p: ({ ...props }) => <p style={{ color: "#334155" }} className="mb-4 leading-relaxed" {...props} />,
                      blockquote: ({ ...props }) => (
                        <blockquote 
                          style={{ 
                            borderLeftColor: "#7A1F2B", 
                            backgroundColor: "#f8fafc", 
                            color: "#475569" 
                          }} 
                          className="border-l-4 p-4 my-6 italic rounded-r-xl" 
                          {...props} 
                        />
                      ),
                      ul: ({ ...props }) => <ul style={{ color: "#334155" }} className="list-disc pl-5 my-2 space-y-1" {...props} />,
                      ol: ({ ...props }) => <ol style={{ color: "#334155" }} className="list-decimal pl-5 my-2 space-y-1" {...props} />,
                      li: ({ ...props }) => <li style={{ color: "#334155" }} className="pl-1" {...props} />,
                      hr: ({ ...props }) => <hr className="my-8 border-t-2 border-strivre-lightnavy" style={{ borderColor: "#e2e8f0" }} {...props} />,
                      table: ({ ...props }) => <table className="w-full border-collapse border border-slate-300 my-6 text-sm" {...props} />,
                      thead: ({ ...props }) => <thead className="bg-strivre-softgrey" {...props} />,
                      th: ({ ...props }) => <th className="border border-slate-300 px-4 py-2 text-left font-bold" style={{ color: "#0f172a" }} {...props} />,
                      td: ({ ...props }) => <td className="border border-slate-300 px-4 py-2 text-slate-600" {...props} />
                    }}
                >
                    {cleanMarkdown(getFullMarkdown())}
                </ReactMarkdown>

                {/* Approve/Decline Buttons Footer */}
                <>
                  <hr className="my-10 border-t-2 border-strivre-lightnavy" />
                  
                  <div className="mb-6">
                    <h2 className="text-2xl font-black text-slate-900 mb-2">Next Step</h2>
                    <p className="text-slate-600 leading-relaxed">
                      If you are happy with this proposal, please proceed with the service confirmation below.
                    </p>
                  </div>

                  <div className="flex gap-4 mt-6">
                    <button
                      onClick={() => publicLink && window.open(`${publicLink}?showForm=true`, '_blank')}
                      className="flex items-center justify-center gap-2 flex-1 bg-strivre-burgundy text-white px-6 py-3.5 rounded-xl font-bold shadow-lg active:scale-95dow-lg shadow-emerald-200 hover:bg-emerald-700 transition-all active:scale-95"
                    >
                      Approve
                    </button>
                    <button
                      onClick={handleDecline}
                      disabled={declining || initialData?.status !== 1}
                      className="flex items-center justify-center gap-2 flex-1 border border-red-300 text-red-600 px-6 py-3.5 rounded-xl font-bold hover:bg-red-50 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {declining ? <Spinner size="h-5 w-5" /> : null}
                      Decline
                    </button>
                  </div>
                </>
                </div>
            ) : (
                <div className="flex flex-col gap-4">
                {/* HEADER PREVIEW */}
                <div
                    className="
                    p-6 bg-slate-50 rounded-2xl border border-slate-100
                    pointer-events-none text-left

                    prose prose-sm max-w-none

                    prose-headings:!text-slate-900
                    prose-h1:!text-slate-900
                    prose-h2:!text-slate-900
          prose-p:!text-slate-900 
          prose-p:!opacity-100
          prose-strong:!text-slate-900 
          prose-strong:!opacity-100
                    "
                >
                    <ReactMarkdown
                    remarkPlugins={[remarkGfm, remarkBreaks]}
                    components={{
                        h1: ({ children, ...props }) => <h1 style={{ color: "#0f172a" }} {...props}>{children}</h1>,
                        h2: ({ children, ...props }) => <h2 style={{ color: "#0f172a" }} {...props}>{children}</h2>,
                        h3: ({ children, ...props }) => <h3 style={{ color: "#0f172a" }} {...props}>{children}</h3>,
                        h4: ({ children, ...props }) => <h4 style={{ color: "#0f172a" }} {...props}>{children}</h4>,
                        p: ({ ...props }) => <p style={{ color: "#0f172a", opacity: 1 }} className="mb-1" {...props} />,
                        blockquote: ({ ...props }) => (
                          <blockquote 
                            style={{ 
                              borderLeftColor: "#7A1F2B", 
                              backgroundColor: "#f1f5f9", 
                              color: "#0f172a" 
                            }} 
                            className="border-l-4 p-3 my-4 italic rounded-r-lg opacity-100" 
                            {...props} 
                          />
                        ),
                        ul: ({ ...props }) => <ul style={{ color: "#0f172a" }} className="list-disc pl-5 my-1 space-y-0.5" {...props} />,
                        li: ({ ...props }) => <li style={{ color: "#0f172a" }} className="pl-1" {...props} />,
                        hr: ({ ...props }) => <hr className="my-6 border-t-2 border-strivre-lightnavy" style={{ borderColor: "#cbd5e1" }} {...props} />,
                        table: ({ ...props }) => <table className="w-full border-collapse border border-slate-300 my-4 text-sm" {...props} />,
                        thead: ({ ...props }) => <thead className="bg-strivre-softgrey" {...props} />,
                        th: ({ ...props }) => <th className="border border-slate-300 px-4 py-2 text-left font-bold" style={{ color: "#0f172a" }} {...props} />,
                        td: ({ ...props }) => <td className="border border-slate-300 px-4 py-2" style={{ color: "#0f172a" }} {...props} />
                    }}
                    >
                    {cleanMarkdown(getHeaderMarkdown())}
                    </ReactMarkdown>
                </div>

                {/* TEXTAREA */}
                <textarea
                    ref={textAreaRef}
                    className="input-base min-h-[300px] font-mono text-sm leading-relaxed p-6 focus:ring-2 focus:ring-strivre-burgundy transition-all"
                    placeholder={"Enter the body of your proposal here...\n\nExample:\n## Table of Contents\n1. Project Scope"}
                            value={formData.content}
                            onChange={(e) =>
                            setFormData({ ...formData, content: e.target.value })
                            }
                    />
                        </div>
                )}
            </div>
          </div>
        </div>

        <div className="bg-slate-50 p-6 sm:px-10 border-t border-slate-100 flex flex-col sm:flex-row justify-end gap-4">
          <button 
            disabled={loading}
            onClick={() => handleSubmit(false)}
            className="flex items-center justify-center gap-2 px-8 py-4 bg-white border border-strivre-lightnavy rounded-2xl font-bold text-slate-700 hover:bg-slate-50 transition-all active:scale-95 shadow-sm"
          >
            <Save size={18} /> Save as Draft
          </button>
          <button
            type="button"
            disabled={loading}
            onClick={() => handleSubmit(true)}
            className="btn-primary btn-md inline-flex items-center justify-center gap-2 rounded-lg disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? (
              <>
                <Spinner size="h-5 w-5" />
                Sending...
              </>
            ) : (
              <>
                <Send size={18} />
                Send Proposal
              </>
            )}
        </button>
        </div>
      </div>
    </div>
  );
}
